import {
  DMAutomation,
  DMAutomationState,
  PlanTypeV4,
  SelectRecipientGroupType,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { useCallback, useMemo, useState } from "react";
import filter from "lodash.filter";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { GenericActionItem } from "../../../../DisplayItem/GenericActionItem";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../../utils/colors";
import { GetDateWithTime, hasSubscription } from "@markit/common.utils";
import EmptyStateButton from "../../../../Buttons/EmptyStateButton";
import ListTabs from "../../../../DropdownsAndTabs/ListTabs";
import { useOnMount } from "../../../../../hooks/useOnMount";
import { useLoadDMAutomationItems } from "../../../../../hooks/useLoadDMAutomationItems";
import VirtuosoList from "../../../../Lists/VirtuosoList";
import { hasSocialAccountConnection } from "../../../../../utils/socialAccountUtils";
import EmptyStateButtonSubscription from "../../../../Buttons/EmptyState/EmptyStateButtonSubscription";
import { useNavigate } from "react-router-dom";
import { NavigationId } from "../../../../../navigation/AppParamList";
import SocialProfilePicsLabel from "../../../../DisplayItem/AutoDM/SocialProfilePicsLabel";
import { SocialPlatformIconItem } from "../../../../DisplayItem/AutoDM/SocialPlatformIconItem";

type SelectRecipientsAutoDMLibraryProps = {
  updateSelectType: (selectType: Partial<SelectRecipientGroupType>) => void;
};

const SelectRecipientsAutoDMLibrary = (
  props: SelectRecipientsAutoDMLibraryProps
) => {
  const { updateSelectType } = props;
  const navigate = useNavigate();
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountState).account;
  // A creator with only a Facebook Page connected still has automations to pick from
  const hasConnectedSocialAccount =
    hasSocialAccountConnection(instagramAccounts) ||
    hasSocialAccountConnection(facebookPages);
  const [searchTerm, setSearchTerm] = useState("");
  const [stateValue, setStateValue] = useState(0);

  const {
    fetchedDMAutomations,
    isLoading,
    isFinished,
    loadDMAutomations,
    loadMoreDMAutomations,
  } = useLoadDMAutomationItems({
    userId: accountData.uid,
    windowSize: 15,
    onlyCampaignDMAutomations: true,
    trustedSender: accountData.trustedSender,
  });

  useOnMount(() => {
    loadDMAutomations();
  });

  const visibleAutomations = useMemo(
    () =>
      fetchedDMAutomations.filter((automation) =>
        stateValue === 0
          ? automation.state === DMAutomationState.ACTIVE
          : automation.state === DMAutomationState.INACTIVE
      ),
    [fetchedDMAutomations, stateValue]
  );

  const containsItem = useCallback((item: DMAutomation, query: string) => {
    return item.title.toLowerCase().includes(query.toLowerCase());
  }, []);

  const itemsToShow: DMAutomation[] = useMemo(() => {
    const items = hasConnectedSocialAccount ? visibleAutomations : [];
    if (searchTerm === "") {
      return items;
    }
    return filter(items, (item: DMAutomation) =>
      containsItem(item, searchTerm)
    );
  }, [hasConnectedSocialAccount, visibleAutomations, searchTerm, containsItem]);

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 10, paddingBottom: 14 }}>
        <SearchBoxContainer
          value={searchTerm}
          placeholder={"Search..."}
          onChange={(e) => setSearchTerm(e.target.value)}
          containerStyles={{ marginTop: 0 }}
        />
        <ListTabs
          tabLabels={["Active", "Inactive"]}
          tabNumbers={[-1, -1]}
          selectedValue={stateValue}
          onChange={setStateValue}
          altColor={Colors.BLACK}
        />
      </div>
      <VirtuosoList
        items={itemsToShow}
        loadMoreOnPress={loadMoreDMAutomations}
        renderItem={(item: DMAutomation) => (
          <>
            <GenericActionItem
              leftComp={
                <SocialPlatformIconItem
                  platform={item.platform}
                  size={40}
                  noBorder
                />
              }
              textComp={
                <div className="ColumnNormalSelect" style={{ gap: 4 }}>
                  <span className="OneLineText bodyMedium">{item.title}</span>
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <SocialProfilePicsLabel
                      igAccountIds={item.igAccountIds}
                      platform={item.platform}
                    />
                  </div>
                  <span
                    className="smallBodySubtext OneLineText"
                    style={{ width: 340 }}
                  >
                    Created: {GetDateWithTime(item.createdAt, true)}
                  </span>
                </div>
              }
              rightComp={
                <Icon
                  icon="ion:chevron-right"
                  height={16}
                  style={{ color: Colors.GRAY1 }}
                />
              }
              onPress={() => updateSelectType({ selectedItem: item })}
            />
            <HorizontalDivider altMargin={10} />
          </>
        )}
        isLoading={isLoading}
        isFinished={isFinished}
        searchTerm={searchTerm}
        renderNonSearchEmptyView={
          !hasConnectedSocialAccount ? (
            !hasSubscription(accountData) ? (
              <EmptyStateButtonSubscription
                planType={PlanTypeV4.ESSENTIAL}
                iconName={"fa7-solid:plug"}
                description="Create automations to convert your audience from social media to Markit"
                btnText="Get Essential Plan"
              />
            ) : (
              <EmptyStateButton
                title="Connect to Start Automations"
                description="Create automations to convert your audience from social media to Markit"
                icon={<Icon icon={"fa7-solid:plug"} height={63} />}
                iconBox={80}
                containerStyles={{ paddingTop: 120 }}
                onPress={() => navigate(NavigationId.HOME_AUTO_DM)}
                btnText="Connect"
              />
            )
          ) : (
            <EmptyStateButton
              title={"No Automations"}
              description={"Your Auto DM automations will show up here"}
              icon={
                <Icon
                  icon="ion:flash"
                  height={64}
                  style={{ color: Colors.GRAY1 }}
                />
              }
              iconBox={84}
              containerStyles={{ paddingTop: 100 }}
            />
          )
        }
        renderHeader={null}
      />
    </>
  );
};

export default SelectRecipientsAutoDMLibrary;
